/**
 * Streaks, derived rather than stored.
 *
 * Every clear ever recorded is already in history with its timestamp, and
 * nothing prunes it - so the count is a pure function of data we have, and it
 * is right retroactively. A player who has been clearing boards for months sees
 * that streak the first time this ships, instead of starting from day one.
 *
 * Days are the player's own calendar days. Date arithmetic goes through
 * setDate, so months, leap years and daylight saving all fall out correctly
 * rather than being approximated by adding 24 hours.
 */
import GameStats from './GameStats.js';

const startOfDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const dayKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const shift = (date, days) => {
  const moved = new Date(date);
  moved.setDate(moved.getDate() + days);
  return moved;
};

/** Every local day on which at least one board was cleared, keyed for lookup */
function clearedDays(entries) {
  const days = new Map();
  entries.forEach(({ clears = [] }) => {
    clears.forEach((timestamp) => {
      const day = startOfDay(timestamp);
      days.set(dayKey(day), day);
    });
  });
  return days;
}

/**
 * Days in a row up to and including today.
 * @param {Object[]} entries - from GameStats.getHistoryEntries()
 * @param {number} now
 * @returns {number}
 */
export function currentStreak(entries, now = Date.now()) {
  const days = clearedDays(entries);
  if (days.size === 0) return 0;

  let cursor = startOfDay(now);
  // Today is not over yet. Not having cleared one so far cannot break a streak
  // that is still alive - miss this and the count reads zero every morning.
  if (!days.has(dayKey(cursor))) cursor = shift(cursor, -1);

  let streak = 0;
  while (days.has(dayKey(cursor))) {
    streak += 1;
    cursor = shift(cursor, -1);
  }
  return streak;
}

/**
 * The best run ever. Costs nothing to derive, and gives someone who lapsed
 * something to see other than a zero.
 * @param {Object[]} entries
 * @returns {number}
 */
export function longestStreak(entries) {
  const days = [...clearedDays(entries).values()].sort((a, b) => a - b);

  let best = 0;
  let run = 0;
  days.forEach((day, index) => {
    const previous = days[index - 1];
    run = previous && dayKey(shift(previous, 1)) === dayKey(day) ? run + 1 : 1;
    best = Math.max(best, run);
  });
  return best;
}

/** Whether today already counts, which is the only thing the player can act on */
export function clearedToday(entries, now = Date.now()) {
  return clearedDays(entries).has(dayKey(startOfDay(now)));
}

/**
 * How many boards were cleared today. One means the clear that just landed is
 * the one that put today on the streak - which is the difference between "you
 * moved the count" and "this was already counted", and the two should not be
 * celebrated the same way.
 */
export function clearsToday(entries, now = Date.now()) {
  const today = dayKey(startOfDay(now));
  return entries.reduce(
    (total, { clears = [] }) =>
      total + clears.filter((timestamp) => dayKey(startOfDay(timestamp)) === today).length,
    0
  );
}

/** The whole picture off real history - tutorial boards are excluded there */
export function readStreak(now = Date.now()) {
  const entries = GameStats.getHistoryEntries();
  return {
    current: currentStreak(entries, now),
    longest: longestStreak(entries),
    today: clearedToday(entries, now),
  };
}
